import React from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, ShieldCheck, Heart, ArrowRight, Star, Flower2 } from "lucide-react";
import { SITE, IMAGES, TESTIMONIALS } from "@/lib/site";
import PriceBanner from "@/components/PriceBanner";
import SectionTitle from "@/components/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";

const SERVICES_PREVIEW = [
  { icon: Heart, title: "Organisation d'obsèques", desc: "Cérémonies civiles et religieuses, inhumation ou crémation, dans le respect des volontés." },
  { icon: Flower2, title: "Marbrerie & Ornements", desc: "Tombes, pierres, plaques, fleurs, urnes et gravures soignées." },
  { icon: ShieldCheck, title: "Prévoyance & Capital décès", desc: "Contrats obsèques et accompagnement pour libérer rapidement les fonds." },
];

export default function HomePage() {
  usePageMeta(
    "Pompes funèbres Perpignan & Baho — Disponibles 24h/24",
    "Pompes Funèbres à Perpignan, Baho, Saint-Estève, Rivesaltes, Cabestany, Canet-en-Roussillon et tout le 66. Organisation d'obsèques, marbrerie, prévoyance. 7j/7 — 24h/24. Obsèques dès 1 997 €. ☎ 06 24 29 91 00."
  );
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[88vh] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroCandle}
            alt="Bougie blanche allumée — symbole de recueillement"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>

        <div className="relative container-soft py-20 lg:py-28 text-white">
          <div className="max-w-3xl">
            <p className="overline text-white/70 reveal">{SITE.badge}</p>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mt-6 reveal reveal-delay-1">
              À vos côtés dans les <em className="italic font-light">moments les plus difficiles</em>.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed reveal reveal-delay-2">
              Un accompagnement humain, du premier appel jusqu'au dernier hommage.
              Parce que chaque hommage mérite respect et dignité.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal reveal-delay-3">
              <a
                href={SITE.phoneHref}
                data-testid="hero-call-button"
                className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-200 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Phone size={18} strokeWidth={1.5} />
                Nous appeler — {SITE.phone}
              </a>
              <Link
                to="/contact"
                data-testid="hero-contact-link"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-white/10 transition-all duration-300"
              >
                Devis gratuit <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-3 text-white/80 reveal reveal-delay-4">
              <Clock size={16} strokeWidth={1.5} />
              <span className="text-sm tracking-wide">{SITE.availability}</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO QUOTE */}
      <section className="py-24 lg:py-32">
        <div className="container-soft grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">Notre engagement</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.1]">
              Un accompagnement humain, <em className="italic font-light">sincère</em> et respectueux.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-black/10">
            <p className="text-lg leading-relaxed text-neutral-700">
              Perdre un proche est l'épreuve d'une vie. Notre rôle est de vous décharger
              des démarches, d'écouter vos souhaits et de rendre l'hommage juste —
              sans jargon, sans précipitation, sans surprise.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-neutral-700">
              De la première formalité jusqu'à la dernière fleur déposée, nous restons
              auprès de vous. <strong className="font-medium">7 jours sur 7, 24 heures sur 24.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft">
          <SectionTitle
            overline="Nos services"
            title="Une prise en charge complète, du premier instant au recueillement."
            kicker="Civiles ou religieuses, simples ou solennelles : nous adaptons chaque cérémonie aux volontés de la famille."
          />
          <div className="grid md:grid-cols-3 gap-px bg-black/10 mt-16 border border-black/10">
            {SERVICES_PREVIEW.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`service-preview-${i}`}
                  className="bg-white p-10 hover:bg-neutral-50 transition-colors duration-500"
                >
                  <Icon size={28} strokeWidth={1.25} />
                  <h3 className="font-serif-display text-2xl mt-6">{s.title}</h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="home-services-link"
              className="inline-flex items-center gap-2 text-sm tracking-wide border-b border-black pb-1 hover:opacity-70 transition-opacity"
            >
              Découvrir tous nos services <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICE BANNER */}
      <PriceBanner />

      {/* COMMITMENTS */}
      <section className="py-24 lg:py-32">
        <div className="container-soft grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="overline">Nos valeurs</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              Respect, discrétion, transparence.
            </h2>
            <ul className="mt-10 space-y-5">
              {[
                "Respect absolu des volontés et des croyances",
                "Discrétion et confidentialité à chaque étape",
                "Transparence des prix, devis détaillé écrit",
                "Accompagnement personnalisé, sans pression",
              ].map((v, i) => (
                <li key={i} className="flex items-start gap-4 pb-5 border-b border-black/10">
                  <span className="font-serif-display text-2xl text-black/40 leading-none mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-neutral-800 leading-relaxed">{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <img
              src={IMAGES.hands}
              alt="Mains se tenant — symbole d'accompagnement"
              className="w-full aspect-[4/5] object-cover grayscale"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="bg-black text-white py-24 lg:py-32">
        <div className="container-soft">
          <SectionTitle
            overline="Avis clients"
            title="La confiance des familles que nous avons accompagnées."
            kicker="Quelques mots reçus, qui nous touchent et nous engagent."
            invert
          />
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            {TESTIMONIALS.slice(0, 2).map((t, i) => (
              <figure
                key={i}
                data-testid={`home-testimonial-${i}`}
                className="border border-white/15 p-10 hover:border-white/40 transition-colors duration-500"
              >
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} size={14} strokeWidth={1.25} fill="white" />
                  ))}
                </div>
                <blockquote className="mt-6 font-serif-display text-2xl leading-snug">
                  « {t.text} »
                </blockquote>
                <figcaption className="mt-8 text-sm text-white/60">
                  — {t.name}, {t.city}
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/avis"
              data-testid="home-avis-link"
              className="inline-flex items-center gap-2 text-sm tracking-wide border-b border-white pb-1 hover:opacity-70 transition-opacity"
            >
              Lire tous les avis <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ZONES D'INTERVENTION — SEO LOCAL */}
      <section data-testid="zones-intervention" className="py-24 lg:py-32 border-t border-black/10">
        <div className="container-soft">
          <SectionTitle
            overline="Zones d'intervention"
            title="Pompes funèbres à Perpignan, Baho et dans tout le 66."
            kicker="Notre maison intervient dans l'ensemble des Pyrénées-Orientales. Quel que soit le lieu du décès, nous nous déplaçons rapidement pour prendre en charge votre proche et organiser la cérémonie."
            align="center"
          />
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {[
              "Perpignan",
              "Baho",
              "Saint-Estève",
              "Rivesaltes",
              "Bompas",
              "Pia",
              "Cabestany",
              "Canet-en-Roussillon",
              "Le Soler",
              "Toulouges",
              "Saint-Cyprien",
              "Argelès-sur-Mer",
              "Le Boulou",
              "Thuir",
              "Elne",
              "Pollestres",
              "Villelongue-de-la-Salanque",
              "Saleilles",
              "Bompas",
              "Espira-de-l'Agly",
            ].filter((c, i, arr) => arr.indexOf(c) === i).map((city) => (
              <div
                key={city}
                className="bg-white py-6 px-4 text-center hover:bg-neutral-50 transition-colors duration-500"
              >
                <span className="font-serif-display text-lg">{city}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-neutral-600 text-sm max-w-2xl mx-auto leading-relaxed">
            Votre commune n'apparaît pas ? Nous intervenons dans <strong className="font-medium">l'ensemble du département des Pyrénées-Orientales (66)</strong>. Appelez-nous au <a href={SITE.phoneHref} className="underline">{SITE.phone}</a>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="container-soft text-center">
          <SectionTitle
            overline="Une question ?"
            title="Nous sommes joignables à toute heure."
            kicker="Un appel suffit. Nous vous répondons immédiatement, avec calme et clarté."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="home-cta-call"
              className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300"
            >
              <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
            </a>
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300"
            >
              Écrire un message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
